import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import TuneIcon from "@mui/icons-material/Tune";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import FilterDropdown from "@/components/collections/FilterDropdown";
import { SECONDARY_BUTTON_CLASS } from "@/constants/buttons";
import type { TypeTab } from "@/constants/redemption";
import { useFullscreenPreview } from "@/context/FullscreenPreviewContext";
import { filterOffersChoice } from "@/utils/redemption-helper";

interface RedemptionFilterBarProps {
  /** Mobile layout. A prop, not a `md:` variant — the builder renders this bar inside a
   *  ~390px preview frame, where viewport-based variants would still resolve to desktop. */
  isMobile: boolean;
  typeTabs: TypeTab[];
  activeType: string;
  onChooseType: (value: string) => void;
  /** GFG gift categories on offer (see availableGiftCategories). */
  categories: string[];
  /** Categories currently picked (multi-select); empty = all. */
  activeCats: string[];
  /** Toggle one category on/off. */
  onChooseCat: (value: string) => void;
  /** Clear every category pick. */
  onClearCats: () => void;
  causes: string[];
  /** Causes currently picked (multi-select); empty = all. */
  activeCauses: string[];
  /** Toggle one cause on/off. */
  onChooseCause: (value: string) => void;
  /** Clear every cause pick. */
  onClearCauses: () => void;
  favOnly: boolean;
  onToggleFav: () => void;
  /** Opens the mobile filter sheet — owned higher up (RedemptionCollection), like a modal. */
  onOpenSheet: () => void;
}

const PILL_MASK: Record<string, string> = {
  right: "[mask-image:linear-gradient(to_right,#000_calc(100%-28px),transparent)]",
  left: "[mask-image:linear-gradient(to_right,transparent,#000_28px)]",
  both: "[mask-image:linear-gradient(to_right,transparent,#000_28px,#000_calc(100%-28px),transparent)]",
};

/**
 * Recipient collection filter bar. Desktop: underline type tabs + Category/Cause
 * dropdown pills + a Favorites toggle. Mobile: a horizontally-scrollable pill row
 * (edge-faded) + one "Filters" tool that opens a bottom sheet. Ported from the
 * prototype's `.lp-filter-*` / `.fsheet-*` markup to Tailwind + design tokens.
 *
 * Owns only its pill-row edge-fade UI state; the filter *values* and the mobile
 * filter sheet live in the parent (RedemptionCollection), which opens the sheet
 * via `onOpenSheet` — the sheet is a screen-covering modal, so it's owned high up.
 */
export default function RedemptionFilterBar({
  isMobile,
  typeTabs,
  activeType,
  onChooseType,
  categories,
  activeCats,
  onChooseCat,
  onClearCats,
  causes,
  activeCauses,
  onChooseCause,
  onClearCauses,
  favOnly,
  onToggleFav,
  onOpenSheet,
}: RedemptionFilterBarProps) {
  const [fades, setFades] = useState({ left: false, right: false });
  const tabsRef = useRef<HTMLDivElement>(null);
  const preview = useFullscreenPreview();
  const setExitInHeader = preview?.setExitInHeader;
  const [pinned, setPinned] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  const activeFilterCount = activeCats.length + activeCauses.length + (favOnly ? 1 : 0);

  // Edge-fade the mobile pill row only on a side that has hidden pills.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `isMobile` is an intentional trigger the body doesn't read — flipping it swaps the row between scrollable and overflow-visible, so the fades must be re-measured.
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const update = () => {
      const more = el.scrollWidth - el.clientWidth > 2;
      setFades({
        left: more && el.scrollLeft > 2,
        right: more && el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [isMobile]);

  // Observes this bar, not a sentinel: the `-1px` top margin clips 1px off the pinned bar so its
  // ratio leaves 1, making every pin/unpin a threshold crossing however far one frame scrolled.
  useEffect(() => {
    const root = preview?.scrollRoot;
    const el = barRef.current;
    if (!root || !el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        // Both setters in one callback, so React commits the swap as one frame — never two Exits.
        const next =
          entry.intersectionRatio > 0 &&
          entry.boundingClientRect.top <= (entry.rootBounds?.top ?? 0);
        setPinned(next);
        setExitInHeader?.(next);
      },
      { root, rootMargin: "-1px 0px 0px 0px", threshold: [0, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [preview?.scrollRoot, setExitInHeader]);

  // Release on unmount (a preview stage switch), or the overlay's floating pill stays hidden.
  useEffect(() => () => setExitInHeader?.(false), [setExitInHeader]);

  const maskClass =
    fades.left && fades.right
      ? PILL_MASK.both
      : fades.right
        ? PILL_MASK.right
        : fades.left
          ? PILL_MASK.left
          : "";

  return (
    <div ref={barRef} className="sticky top-0 z-50 border-b border-card-border bg-white">
      <div
        className={`mx-auto flex max-w-300 items-center justify-between ${isMobile ? "px-4" : "px-6 max-lg:flex-wrap"}`}
      >
        {/* Type tabs — mobile: scrollable pills; desktop: underline tabs. */}
        <div
          ref={tabsRef}
          className={
            isMobile
              ? // py-[3.2px] (was 0.5 = 2px) pushes the row right up to the 44px the Filters button
                // already sets as the header's height ceiling, without exceeding it — that's where
                // the pill's invisible hit area below lives, so `overflow-x-auto`'s auto-coerced
                // y-clip crops it at 44px tall (still no visible row-height change) instead of 38.
                `flex min-w-0 flex-1 gap-2 overflow-x-auto py-[3.2px] scrollbar-none [&::-webkit-scrollbar]:hidden ${maskClass}`
              : "flex min-w-0 flex-none gap-0 overflow-visible py-0 scrollbar-none [&::-webkit-scrollbar]:hidden"
          }
        >
          {typeTabs.map((t) => {
            const active = activeType === t.value;
            return (
              <button
                key={t.value}
                type="button"
                data-type={t.value}
                aria-pressed={active}
                onClick={() => onChooseType(t.value)}
                className={`${
                  isMobile
                    ? // Invisible hit area: the pill stays 38px tall. The pseudo-element's `top`/
                      // `bottom` sit against the button's PADDING edge (its own 1px `border` shrinks
                      // the containing block), so `-8px` deliberately overshoots — the row's own
                      // overflow clip (see py-[3.2px] above) still caps it there, at 44px (R05).
                      "relative flex-none cursor-pointer whitespace-nowrap rounded-full border px-3.75 py-2.25 text-[11px] font-bold uppercase tracking-[0.8px] transition-colors before:absolute before:inset-x-0 before:content-[''] before:top-[-8px] before:bottom-[-8px]"
                    : "flex-none cursor-pointer whitespace-nowrap rounded-none border-0 border-b-2 px-4 py-4.5 text-[10px] font-bold uppercase tracking-[1.5px] transition-colors"
                } ${
                  active
                    ? isMobile
                      ? "border-rodeo-black bg-rodeo-black text-white"
                      : "bg-transparent text-(--lp-accent,var(--color-rodeo-black)) border-(--lp-accent,var(--color-rodeo-black))"
                    : isMobile
                      ? "border-smoke text-graphite hover:text-rodeo-black"
                      : "border-transparent text-graphite hover:text-rodeo-black"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Desktop: Category / Cause dropdowns (the shared collections FilterDropdown,
            multi-select via a raw-category `selected` list — its label shows the pick count
            and each row is a checkbox) + a Favorites toggle styled to match the triggers. */}
        {!isMobile && (
          // `max-lg:` moves this group to its own right-aligned row once the tabs force a wrap (R04).
          <div className="flex items-center gap-2 max-lg:my-1.5 max-lg:ml-auto">
            {/* Category / Cause each render only when they offer a real choice (≥2 options) —
                on the Charity/Experiences tabs the lone option is already implied by the tab. */}
            {filterOffersChoice(categories) && (
              <FilterDropdown
                label="Category"
                options={categories}
                selected={activeCats}
                onToggle={onChooseCat}
                onClear={onClearCats}
              />
            )}
            {filterOffersChoice(causes) && (
              <FilterDropdown
                label="Cause"
                options={causes}
                selected={activeCauses}
                onToggle={onChooseCause}
                onClear={onClearCauses}
              />
            )}
            <button
              type="button"
              aria-pressed={favOnly}
              onClick={onToggleFav}
              className={twMerge(
                "flex h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-white px-4 transition-colors",
                SECONDARY_BUTTON_CLASS,
                favOnly ? "border-primary-active" : "hover:border-primary-active",
              )}
            >
              {favOnly ? (
                <FavoriteIcon sx={{ fontSize: 16 }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: 16 }} />
              )}
              Favorites
            </button>
          </div>
        )}

        {/* Mobile: single Filters tool → bottom sheet. Light-dust circle, dark-dust
            icon; an inset dark-dust ring marks it active (matches `.lp-filter-more`). */}
        {isMobile && (
          <button
            type="button"
            aria-haspopup="dialog"
            aria-label="Filter gifts"
            onClick={onOpenSheet}
            className={`relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-light-dust text-dark-dust ${
              activeFilterCount > 0 ? "shadow-[inset_0_0_0_1.5px_var(--color-dark-dust)]" : ""
            }`}
          >
            <TuneIcon sx={{ fontSize: 20 }} />
            {activeFilterCount > 0 && (
              <span className="absolute -top-0.75 -right-0.75 flex h-4.75 min-w-4.75 items-center justify-center rounded-full border-2 border-white bg-dark-dust px-1 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        )}

        {/* Once pinned, host the overlay's Exit control here so its fixed pill stops
          overlapping this header. `preview` is null outside the preview overlay. */}
        {pinned && preview && (
          <div className="ml-2 flex shrink-0 items-center">{preview.exitControl}</div>
        )}
      </div>
    </div>
  );
}
